/**
 * deriveSpunOffFromLabel — build the RecipeHeader `spunOffFromLabel` prose from
 * a recipe's group-share provenance (onboarding revival T4 §5).
 *
 * Cross-agent contract (fixed): the recipe summary DTO gains an OPTIONAL
 * `spunOffFrom?: { handle: string | null; groupName: string | null }`, captured
 * at import time only for recipes adopted from a group share. Absent field →
 * render nothing (this returns `undefined`).
 *
 * Label shape (design.md / RecipeHeader §2a): "spun off from @<handle> ·
 * <groupName>". Either half may be missing and is omitted gracefully:
 *   - both      → "spun off from @otto · Family"
 *   - handle    → "spun off from @otto"
 *   - group     → "spun off from Family"
 *   - neither   → undefined (no chrome)
 *
 * Voice (branding.md §8 / §5.2.1): "spun off" is the sanctioned spin-off
 * vocabulary; no exclamation, sentence-case prose.
 */

export interface SpunOffFromProvenance {
  readonly handle: string | null;
  readonly groupName: string | null;
}

export function deriveSpunOffFromLabel(
  provenance: SpunOffFromProvenance | null | undefined,
): string | undefined {
  if (!provenance) return undefined;

  const handleRaw = provenance.handle?.trim() ?? "";
  const groupName = provenance.groupName?.trim() ?? "";

  // Normalise the handle to a single leading "@" (the contract carries the bare
  // handle, but guard against a stored value that already includes it).
  const handlePart = handleRaw.length > 0 ? `@${handleRaw.replace(/^@+/, "")}` : "";

  const parts = [handlePart, groupName].filter((p) => p.length > 0);
  if (parts.length === 0) return undefined;

  return `spun off from ${parts.join(" · ")}`;
}
